import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { AssignmentReturn, Clear } from "@mui/icons-material";
import { SheqPpeRecord } from "../../../redux/sheqApi";
import { generateReturnSlip } from "./generateReturnSlip";

const NAVY = "#123456";

const STATUS_COLOR: Record<string, string> = {
  RETURNED: "#1565c0",
  DAMAGED: "#c62828",
  LOST: "#e65100",
  EXPIRED: "#6d4c41",
};

interface Props {
  open: boolean;
  record: SheqPpeRecord | null;
  onClose: () => void;
}

export const PpeReturnSlipDialog: React.FC<Props> = ({
  open,
  record,
  onClose,
}) => {
  const sigRef = useRef<SignatureCanvas>(null);
  const [receivingOfficer, setReceivingOfficer] = useState("");
  const [generating, setGenerating] = useState(false);

  React.useEffect(() => {
    if (open) {
      setReceivingOfficer(record?.issuedByName ?? "");
      setTimeout(() => sigRef.current?.clear(), 80);
    }
  }, [open, record]);

  const handleGenerate = async () => {
    if (!record) return;
    setGenerating(true);
    try {
      const sig = sigRef.current;
      const signatureDataUrl =
        sig && !sig.isEmpty()
          ? sig.getCanvas().toDataURL("image/png")
          : undefined;

      let logoDataUrl: string | undefined;
      try {
        const resp = await fetch("/logo192.png");
        const blob = await resp.blob();
        logoDataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        /* logo optional */
      }

      await generateReturnSlip({
        record,
        signatureDataUrl,
        receivingOfficerName: receivingOfficer.trim() || record.issuedByName,
        logoDataUrl,
      });

      onClose();
    } finally {
      setGenerating(false);
    }
  };

  if (!record) return null;

  const statusColor = STATUS_COLOR[record.status] ?? "#555";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ bgcolor: NAVY, color: "#fff", py: 1.5, fontSize: 15 }}
      >
        PPE Return Slip
      </DialogTitle>

      <DialogContent>
        <Box pt={2} display="flex" flexDirection="column" gap={2.5}>
          {/* Record summary */}
          <Box
            sx={{
              bgcolor: "#f0f4f8",
              borderRadius: 1.5,
              p: 1.5,
              border: "1px solid #d0dce8",
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="body2" fontWeight={700}>
                {record.workerName}
              </Typography>
              <Box
                component="span"
                sx={{
                  bgcolor: statusColor,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  px: 0.8,
                  py: 0.2,
                  borderRadius: 0.5,
                }}
              >
                {record.status}
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {[record.workerEmployeeNo, record.department, record.blockName]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
            <Box mt={1} display="flex" flexDirection="column" gap={0.25}>
              <Typography variant="caption">
                <b>Item:</b>{" "}
                {record.ppeCode && (
                  <Box
                    component="span"
                    sx={{
                      bgcolor: NAVY,
                      color: "#fff",
                      fontSize: 10,
                      px: 0.6,
                      py: 0.1,
                      borderRadius: 0.5,
                      mr: 0.5,
                    }}
                  >
                    {record.ppeCode}
                  </Box>
                )}
                {record.ppeName}
                {record.size ? ` (${record.size})` : ""}
              </Typography>
              <Typography variant="caption">
                <b>Issued:</b> {record.issuedDate} &nbsp;·&nbsp;{" "}
                <b>Returned:</b> {record.returnDate ?? "—"}
              </Typography>
              {record.returnCondition && (
                <Typography variant="caption">
                  <b>Condition at return:</b> {record.returnCondition}
                </Typography>
              )}
              {record.returnNotes && (
                <Typography variant="caption">
                  <b>Notes:</b> {record.returnNotes}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Receiving officer */}
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color={NAVY}
              display="block"
              mb={0.75}
            >
              RECEIVING OFFICER NAME
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={receivingOfficer}
              onChange={(e) => setReceivingOfficer(e.target.value)}
              placeholder="Officer's name (printed on slip)"
            />
          </Box>

          {/* Signature pad */}
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={0.75}
            >
              <Typography variant="caption" fontWeight={700} color={NAVY}>
                STAFF SIGNATURE (RETURNEE)
              </Typography>
              <Button
                size="small"
                startIcon={<Clear sx={{ fontSize: 14 }} />}
                onClick={() => sigRef.current?.clear()}
                sx={{ fontSize: 11, py: 0.25 }}
              >
                Clear
              </Button>
            </Box>
            <Box
              sx={{
                border: "1px solid #bbb",
                borderRadius: 1,
                bgcolor: "#fff",
                cursor: "crosshair",
                overflow: "hidden",
                lineHeight: 0,
              }}
            >
              <SignatureCanvas
                ref={sigRef}
                penColor="#000"
                canvasProps={{
                  width: 556,
                  height: 120,
                  style: { display: "block", width: "100%", height: 120 },
                }}
                backgroundColor="rgb(255,255,255)"
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Optional — leave blank to print with a blank signature line.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={generating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            generating ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <AssignmentReturn />
            )
          }
          onClick={handleGenerate}
          disabled={generating}
          sx={{ bgcolor: NAVY }}
        >
          {generating ? "Generating…" : "Print Return Slip"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
